import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { faBan, faCalendar, faFileExcel, faReceipt, faSearch, faSync, faTractor, faTruck } from '@fortawesome/free-solid-svg-icons';
import { NbToastrService } from '@nebular/theme';
import { AggregateQueryCellInfoEventArgs, AggregateRowModel, ColumnModel, FilterSettingsModel, GridComponent, GroupSettingsModel, PageSettingsModel } from '@syncfusion/ej2-angular-grids';
import * as moment from 'moment-timezone';
import { finalize } from 'rxjs/operators';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { GrowerReceiptsDTO } from '../../models/grower-receipts-dto';
import { GrowerReceiptsReportService } from '../../services/grower-receipts-report.service';

const MOBILE_PAGE_SIZE = 25;
const GRAND_TOTAL_LABEL = 'Grand total:';

interface GrowerReceiptRow extends GrowerReceiptsDTO {
  dateReceived: Date | null;
}

interface StyleWithValue {
  value: string | number;
}

@Component({
  selector: 'grower-receipts-report',
  templateUrl: './grower-receipts-report.component.html',
  styleUrls: ['./grower-receipts-report.component.scss']
})
export class GrowerReceiptsReportComponent implements OnInit {
  @ViewChild('grid') public grid: GridComponent;

  public faBan = faBan;
  public faCalendar = faCalendar;
  public faFileExcel = faFileExcel;
  public faReceipt = faReceipt;
  public faSearch = faSearch;
  public faSync = faSync;
  public faTractor = faTractor;
  public faTruck = faTruck;

  public isLoading = false;
  public isFiltersLoading = false;
  public hasReport = false;
  public searchTerm = '';
  public filterCount = 0;
  public rows: GrowerReceiptRow[] = [];
  private allRows: GrowerReceiptRow[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;

  public cropYears: number[] = [];
  public commodityNames: string[] = [];
  public selectedYear: number | null = null;
  public selectedNames: string[] = [];

  public pageSettings: PageSettingsModel = { pageSize: 50, pageSizes: [20, 50, 100] };
  public filterSettings: FilterSettingsModel = { mode: 'Immediate', type: 'Excel' };
  public groupSettings: GroupSettingsModel = { columns: ['GROWER'], showGroupedColumn: true, showDropArea: false };
  public aggregates: AggregateRowModel[] = [{
    columns: [
      { type: 'Count', field: 'GROWER', footerTemplate: GRAND_TOTAL_LABEL },
      { type: 'Sum', field: 'RECEIVED_QUANTITY', format: 'N0', footerTemplate: '${Sum}' }
    ]
  }];

  public columns: ColumnModel[] = [
    { field: 'GROWER', headerText: 'Grower', width: 220, clipMode: 'EllipsisWithTooltip' },
    { field: 'Variety', headerText: 'Variety', width: 150 },
    { field: 'ITEM_DESCRIPTION', headerText: 'Item Description', width: 180, clipMode: 'EllipsisWithTooltip' },
    { field: 'dateReceived', headerText: 'Date Received', width: 150, type: 'date', format: 'M/d/y' },
    { field: 'RECEIVING_LOCATION_NAME', headerText: 'Cert Rm', width: 140 },
    { field: 'RECEIVING_TICKET_NBR', headerText: 'Receiving Ticket', width: 160 },
    { field: 'LOT_NUMBER', headerText: 'Lot #', width: 130 },
    { field: 'POOL_CODE', headerText: 'Pool Code', width: 130 },
    { field: 'STORAGE_QUALITY', headerText: 'SQ', width: 90 },
    { field: 'HAULED_BY', headerText: 'Hauled By', width: 150, clipMode: 'EllipsisWithTooltip' },
    { field: 'BOL', headerText: 'BOL', width: 150 },
    { field: 'BIN_TYPE', headerText: 'Bin Type', width: 130 },
    { field: 'TREATMENT', headerText: 'Treat', width: 150, clipMode: 'EllipsisWithTooltip' },
    { field: 'RECEIVED_QUANTITY', headerText: '# Bins', width: 110, format: 'N0', textAlign: 'Right' }
  ];

  private destroyRef = inject(DestroyRef);

  constructor(
    private growerReceiptsReportService: GrowerReceiptsReportService,
    private errorService: ErrorService,
    private toastrService: NbToastrService
  ) { }

  ngOnInit() {
    this.loadFilterOptions();
  }

  loadFilterOptions() {
    this.isFiltersLoading = true;
    let pending = 2;
    const done = () => { if (--pending === 0) this.isFiltersLoading = false; };

    this.growerReceiptsReportService.getCropYears()
      .pipe(finalize(done), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: years => this.cropYears = (years ?? []).map(cy => cy.year).filter((year): year is number => year != null),
        error: err => this.errorService.showError(err)
      });

    this.growerReceiptsReportService.getCommodities()
      .pipe(finalize(done), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: crops => this.commodityNames = (crops ?? []).map(crop => crop.name).filter((name): name is string => !!name),
        error: err => this.errorService.showError(err)
      });
  }

  generateReport() {
    this.isLoading = true;
    this.growerReceiptsReportService.getAllGrowerReceiptsReportsForSteamilt(this.selectedYear ?? 0, this.selectedNames ?? [])
      .pipe(finalize(() => this.isLoading = false), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.allRows = (data ?? []).map(rec => ({
            ...rec,
            dateReceived: rec.DATE_RECEIVED ? moment(rec.DATE_RECEIVED).startOf('day').toDate() : null
          }));
          this.hasReport = true;
          this.applySearch();
        },
        error: err => this.errorService.showError(err)
      });
  }

  onSyncReceipts() {
    this.isLoading = true;
    // generateReport() owns isLoading from here, so no finalize on the sync.
    this.growerReceiptsReportService.syncReceipts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastrService.success('Receipts sync completed successfully', 'Success');
          this.generateReport();
        },
        error: () => {
          this.isLoading = false;
          this.toastrService.danger('Failed to sync receipts', 'Error');
        }
      });
  }

  applySearch() {
    const term = this.searchTerm.trim().toLowerCase();
    this.rows = term
      ? this.allRows.filter(row => [row.GROWER, row.Variety, row.ITEM_DESCRIPTION, row.LOT_NUMBER, row.RECEIVING_TICKET_NBR, row.POOL_CODE, row.BOL, row.HAULED_BY]
        .some(value => value?.toLowerCase().includes(term)))
      : [...this.allRows];
    this.mobileVisibleCount = MOBILE_PAGE_SIZE;
  }

  clearFilters() {
    this.grid?.clearFiltering();
    this.searchTerm = '';
    this.applySearch();
    this.filterCount = 0;
  }

  onActionComplete(args: { requestType?: string }) {
    if (args.requestType === 'filtering')
      this.updateFilterCount();
  }

  onGridDataBound() {
    this.updateFilterCount();
  }

  exportExcel() {
    this.grid?.excelExport({ fileName: 'GrowerReceiptsReport.xlsx' });
  }

  // Export the footer sums as numbers, not the grid's formatted strings.
  aggregateQueryCellInfo(args: AggregateQueryCellInfoEventArgs): void {
    if ((args.type !== 'Footer' && args.type !== 'GroupFooter') || !args.style)
      return;

    const style = args.style as StyleWithValue;
    if (style.value !== GRAND_TOTAL_LABEL)
      style.value = Number(style.value.toString().replace(/[^0-9.-]+/g, ''));
  }

  showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  trackByRow(index: number, row: GrowerReceiptRow): string {
    return `${row.RECEIVING_TICKET_NBR}-${row.LOT_NUMBER}-${index}`;
  }

  private updateFilterCount() {
    const fields = (this.grid?.filterSettings?.columns ?? []).map(c => c.field as string);
    this.filterCount = new Set(fields).size;
  }
}
